import { useMutation, useQueries, useQuery } from 'react-query';
import { httpRequest } from '../config/api';
import {
  GET_AUTO_EXTRACT_STATUSES,
  GET_AUTO_MAP_STATUS,
  GET_CATEGORIES,
  GET_FILE_EXTRACT,
  GET_HEADER_FOOTER,
  GET_SHEET,
  GET_TEMPLATE,
  GET_TRANSFORM,
  GET_WORKFLOW,
  GET_WORKFLOW_BY_ID,
  GET_WORKFLOW_EXTRACT
} from '../consts/queryKeys';

const correlationConfig = () => ({
  headers: {
    'X-Correlation-ID': globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  }
});

export const workflowService = {
  getTransformations: ({ params }) =>
    httpRequest.get('api/transform', {
      params
    }),

  getTransformation: ({ transformId, params }) =>
    httpRequest.get(`api/transform/${transformId}`, {
      params
    }),
  previewTransformation: ({ transformId, params, data }) =>
    httpRequest.post(`api/transform/${transformId}/preview`, data, { params }),
  getTransformationVersions: ({ transformId, params }) =>
    httpRequest.get(`api/transform/${transformId}/versions`, { params }),
  getTransformationVersionChange: ({ transformId, versionId }) =>
    httpRequest.get(`api/transform/${transformId}/versions/${versionId}/compare-change`),
  restoreTransformationVersion: ({ transformId, versionId, data }) =>
    httpRequest.post(
      `api/transform/${transformId}/versions/${versionId}/restore`,
      data,
      correlationConfig()
    ),

  getTransformationsListByPeriod: (periodId, params) =>
    httpRequest.get(`api/workflows/transformations/${periodId}`, { params }),

  replicateAutoExtraction: ({ data }) =>
    httpRequest.post('api/replicate-auto-extraction-transformation', data),
  createTransform: ({ extractId, data, tableName }) =>
    httpRequest.post(
      `api/transform?extract_id=${extractId}&table_name=${tableName}`,
      data,
      correlationConfig()
    ),
  updateTransform: ({ transformId, data }) =>
    httpRequest.put(`api/transform/${transformId}`, data, correlationConfig()),

  getWorkflows: params => httpRequest.get(`api/workflows${params.type || ''}`, params),
  getWorkflowsFunds: params => httpRequest.get(`api/workflows-funds`, { params }),
  getApproverWorkflows: params => httpRequest.get('api/approver-workflows', { params }),
  getGroupedWorkflows: id => httpRequest.get(`api/workflow-groups/${id}`),
  excludeProject: data => httpRequest.post('api/workflow-groups/exclude-workflow-from-group', data),
  updateGroupedWorkflows: data => httpRequest.put(`api/workflow-groups/${data.id}`, data.body),
  createGroup: data => httpRequest.post('api/workflow-groups', data),
  getWorkflowById: (id, params) => httpRequest.get(`api/workflows/${id}`, { ...params }),
  getAutoExtractStatuses: params =>
    httpRequest.get('api/status-auto-extract/', {
      params
    }),
  getAllExtractionsStatus: params =>
    httpRequest.get('api/status-all-extractions/', {
      params
    }),
  getAutoExtractStatusById: id => httpRequest.get(`api/status-auto-extract/${id}`),
  cancelAutoExtract: id => httpRequest.put(`api/cancel-auto-extract/${id}`),
  getWorkflowExtract: params =>
    httpRequest.get('api/workflows/extract/', {
      params
    }),
  getTemplates: ({ params, workflowId }) => httpRequest.get('api/workflows/' + workflowId, params),
  createWorkflow: data => httpRequest.post('api/workflows', data),
  duplicateWorkflow: data => httpRequest.post(`api/workflows/${data.id}/duplicate`, data.body),
  createSheetExtract: data => httpRequest.post('api/extract', data),
  updateSheetExtract: data => httpRequest.put(`api/extract/${data.extractId}/`, data),
  deleteSheetExtract: (extractId, params) =>
    httpRequest.delete(`api/extract/${extractId}/`, {
      params
    }),

  getHeaderFooter: ({ sheetId, params }) =>
    httpRequest.get(`api/sheets/header-footer/${sheetId}`, params),
  getReplicate: ({ params }) =>
    httpRequest.get('api/replicate', {
      params
    }),
  setHeaderFooter: ({ sheetId, data }) =>
    httpRequest.put(`api/sheets/header-footer/${sheetId}`, data),
  updateStructureType: ({ sheetId, data }) =>
    httpRequest.put(`api/sheets/change-structure-type/${sheetId}`, data),

  loadDataToSnowflake: data => httpRequest.post(`api/load-data-to-snowflake?period_id=${data}`),
  autoExtract: data => httpRequest.post('api/auto-extract', data),
  // Auto-Map: detect column→field mappings without running value extraction.
  autoMap: data => httpRequest.post('api/auto-map', data),
  getAutoMapStatus: taskId => httpRequest.get(`api/status-auto-map/${taskId}`),
  reExtractFunds: ({ sheetId, data }) =>
    httpRequest.post(`api/sheets/re-extract-funds/${sheetId}`, data),
  updateWorkflow: ({ data, id }) => httpRequest.put(`api/workflows/${id}`, data),

  deleteWorkflowGroup: id => httpRequest.delete(`api/workflow-groups/${id}`),
  deleteWorkflow: id => httpRequest.delete(`api/workflows/${id}`),
  deleteSheet: sheetId => httpRequest.delete(`api/sheets/${sheetId}`),
  deleteFunds: sheetId => httpRequest.delete(`api/funds/sheet/${sheetId}`),
  deleteQuarter: data =>
    httpRequest.delete(`api/workflows/${data.id}/files`, {
      params: {
        period: data.period,
        year: data.year,
        workflow_id: data.workflowId
      }
    }),

  getWorkflowFunds: params => httpRequest.get('api/funds', params),

  // custom-table
  getCustomTables: params => httpRequest.get('api/custom-tables/', { params }),
  createCustomTable: data => httpRequest.post('api/custom-table/', data),
  deleteCustomTable: id => httpRequest.delete(`api/custom-table/${id}`),
  updateCustomTable: ({ id, data }) => httpRequest.put(`api/custom-table/${id}`, data),

  // global-custom-table
  createGlobalCustomTable: ({ categoryId, data }) =>
    httpRequest.post(`api/workflows/periods/global-custom-tables/${categoryId}`, data),
  deleteGlobalCustomTable: id =>
    httpRequest.delete(`api/workflows/periods/global-custom-tables/${id}`),
  updateGlobalCustomTable: ({ id, data }) =>
    httpRequest.put(`api/workflows/periods/global-custom-tables/${id}`, data),

  // global-custom-fields
  createGlobalCustomField: ({ tableId, data }) =>
    httpRequest.post(`api/workflows/periods/global-custom-fields/${tableId}`, data),
  deleteGlobalCustomField: id =>
    httpRequest.delete(`api/workflows/periods/global-custom-fields/${id}`),

  // custom-category
  createCustomCategory: ({ periodId, data, isFund, workflowId }) =>
    httpRequest.post(
      `api/workflows/periods/custom-categories/${periodId}?is_fund=${isFund}`,
      data,
      {
        headers: {
          workflow_id: workflowId
        }
      }
    ),
  deleteCustomCategory: id => httpRequest.delete(`api/workflows/periods/custom-categories/${id}`),

  //   workflow-fund
  createWorkflowFund: ({ data, params }) =>
    httpRequest.post('api/funds', data, {
      params
    }),
  updateWorkflowFund: ({ data, fundId, params }) =>
    httpRequest.put(`api/funds/${fundId}`, data, {
      params
    }),
  deleteWorkflowFund: ({ fundId, params }) => httpRequest.delete(`api/funds/${fundId}`, { params }),
  getSheet: ({ sheetId, params }) =>
    httpRequest.get(`api/sheets/${sheetId}`, {
      ...params
    }),
  getFileExtract: ({ params }) =>
    httpRequest.get('api/sheets/extract/', {
      params
    }),
  getPdfRegionSelections: ({ params }) =>
    httpRequest.get('api/pdf-region-selections', {
      params
    }),
  upsertPdfRegionSelection: data => httpRequest.post('api/pdf-region-selection', data),
  deletePdfRegionSelection: selectionId =>
    httpRequest.delete(`api/pdf-region-selection/${selectionId}`),
  previewPdfRegion: data => httpRequest.post('api/pdf-region-preview', data),
  createXMLFile: ({ fundId, periodId, params }) =>
    httpRequest.get(`api/${periodId}/funds/${fundId}/create_xml/`, params),
  getWorkflowCategories: params => {
    return httpRequest.get('api/workflows/extract/categories', {
      params
    });
  },
  getCategories: params => httpRequest.get('api/categories/', { params })
};

export const useGetCustomTables = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_CUSTOM_TABLE', params],
    () => workflowService.getCustomTables(params),
    queryParams
  );
};

export const useCreateCustomTable = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.createCustomTable, queryParams);
};

export const useCreateGroup = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.createGroup, queryParams);
};

export const useUpdateCustomTable = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.updateCustomTable, queryParams);
};

export const useDeleteCustomTable = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.deleteCustomTable, queryParams);
};

export const useCreateGlobalCustomTable = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.createGlobalCustomTable, queryParams);
};

export const useUpdateGlobalCustomTable = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.updateGlobalCustomTable, queryParams);
};

export const useDeleteGlobalCustomTable = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.deleteGlobalCustomTable, queryParams);
};

export const useCreateGlobalCustomField = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.createGlobalCustomField, queryParams);
};

export const useDeleteGlobalCustomField = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.deleteGlobalCustomField, queryParams);
};

export const useGetWorkflowCategories = ({ params = {}, queryParams } = {}) => {
  return useQuery(
    ['GET_WORKFLOW_CATEGORIES', params],
    () => workflowService.getWorkflowCategories(params),
    {
      ...queryParams
    }
  );
};

export const useGetWorkflowsList = ({ params = {}, queryParams } = {}) => {
  return useQuery([GET_WORKFLOW, params], () => workflowService.getWorkflows(params), {
    ...queryParams
  });
};

export const useGetWorkflows = ({
  params = {},
  queryParams = {},
  page = 1,
  pageSize = 20,
  search = '',
  filterByAifmType = '',
  filterByAssignedTo = '',
  sortBy = ''
} = {}) => {
  const query = useQuery(
    [
      GET_WORKFLOW,
      'paginated',
      params,
      page,
      pageSize,
      search,
      filterByAifmType,
      filterByAssignedTo,
      sortBy
    ],
    () =>
      workflowService.getWorkflows({
        ...params,
        headers: {
          ...params.headers,
          'X-Schema-Version': '1.0.236' // Required for pagination support
        },
        params: {
          ...params.params,
          page,
          size: pageSize,
          ...(search ? { search } : {}),
          ...(filterByAifmType ? { filter_by_aifm_type: filterByAifmType } : {}),
          ...(filterByAssignedTo ? { filter_by_assigned_to: filterByAssignedTo } : {}),
          ...(sortBy ? { sort_by: sortBy } : {})
        }
      }),
    {
      refetchOnWindowFocus: false,
      ...queryParams
    }
  );

  return {
    ...query,
    workflows: query.data?.items || query.data || [],
    pagination:
      typeof query.data?.total === 'number'
        ? {
            total: query.data.total,
            page: query.data.page || page,
            size: query.data.size || pageSize,
            pages: query.data.pages || 0
          }
        : null
  };
};

export const useGetAutoExtractStatuses = ({ params, queryParams } = {}) => {
  return useQuery(
    [GET_AUTO_EXTRACT_STATUSES, params],
    () => workflowService.getAutoExtractStatuses(params),
    {
      ...queryParams
    }
  );
};

export const useGetReplicateExtraction = ({ params, queryParams } = {}) => {
  return useQuery(
    ['useGetReplicateExtraction', params],
    () => workflowService.getReplicate({ params }),
    {
      ...queryParams
    }
  );
};

export const useGetApproverWorkflows = ({ params, queryParams } = {}) => {
  return useQuery(
    ['useGetApproverWorkflows', params],
    () => workflowService.getApproverWorkflows(params),
    {
      ...queryParams
    }
  );
};

export const useGetAutoExtractStatusById = ({ id, queryParams } = {}) => {
  return useQuery(
    [GET_AUTO_EXTRACT_STATUSES, id],
    () => workflowService.getAutoExtractStatusById(id),
    {
      ...queryParams
    }
  );
};

export const useGetAllStatusExtractions = ({ params, queryParams } = {}) => {
  return useQuery(
    ['useGetAllStatusExtractions', params],
    () => workflowService.getAllExtractionsStatus(params),
    queryParams
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const useGetWorkflowById = ({ id, queryParams = {}, params = {} } = {}) => {
  return useQuery([GET_WORKFLOW_BY_ID, id], () => workflowService.getWorkflowById(id, params), {
    ...queryParams
  });
};

/**
 * The same query, for a list of ids at once.
 *
 * A queue of what is waiting on you is built from periods, and periods only arrive with the
 * workflow — so a screen that reads across projects has to ask for each of them. Same key as
 * `useGetWorkflowById`, so a project already opened on the list costs nothing here, and vice versa.
 */
export const useGetWorkflowsByIds = (ids = [], { enabled = true, staleTime = 60_000 } = {}) =>
  useQueries(
    ids.map(id => ({
      queryKey: [GET_WORKFLOW_BY_ID, id],
      queryFn: () => workflowService.getWorkflowById(id),
      enabled: enabled && !!id,
      staleTime
    }))
  );

export const useGetFileExtract = ({ params, queryParams } = {}) => {
  return useQuery([GET_FILE_EXTRACT, params], () => workflowService.getFileExtract({ params }), {
    ...queryParams
  });
};

export const useGetPdfRegionSelections = ({ params, queryParams } = {}) => {
  return useQuery(
    ['GET_PDF_REGION_SELECTIONS', params],
    () => workflowService.getPdfRegionSelections({ params }),
    {
      ...queryParams
    }
  );
};

export const useUpsertPdfRegionSelection = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.upsertPdfRegionSelection, queryParams);
};

export const useDeletePdfRegionSelection = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.deletePdfRegionSelection, queryParams);
};

export const useGetPdfRegionPreview = ({ data, queryParams } = {}) => {
  return useQuery(['GET_PDF_REGION_PREVIEW', data], () => workflowService.previewPdfRegion(data), {
    ...queryParams
  });
};

export const useGetWorkflowExtract = ({ params, queryParams } = {}) => {
  return useQuery(
    [GET_WORKFLOW_EXTRACT, params],
    () => workflowService.getWorkflowExtract(params),
    {
      ...queryParams
    }
  );
};

export const useAutoExtract = () => {
  return useMutation(workflowService.autoExtract);
};

/** Start an Auto-Map task (POST /api/auto-map). Returns { id, status, sheet_id }. */
export const useAutoMap = () => {
  return useMutation(workflowService.autoMap);
};

/** Poll an Auto-Map task status (GET /api/status-auto-map/{taskId}).
 *  Pass queryParams: { enabled: !!taskId, refetchInterval: 4000 } to poll until completion. */
export const useGetAutoMapStatus = ({ taskId, queryParams = {} } = {}) => {
  return useQuery([GET_AUTO_MAP_STATUS, taskId], () => workflowService.getAutoMapStatus(taskId), {
    enabled: !!taskId,
    ...queryParams
  });
};

export const useExcludeProject = () => {
  return useMutation(workflowService.excludeProject);
};

export const useReplicateAutoExtract = () => {
  return useMutation(workflowService.replicateAutoExtraction);
};

export const useGetSheet = ({ params = {}, queryParams, id, fileId, sheetId } = {}) => {
  return useQuery(
    [GET_SHEET, params, id, fileId, sheetId],
    () => workflowService.getSheet({ params, id, fileId, sheetId }),
    {
      ...queryParams
    }
  );
};

export const useGetCategories = ({ params = {}, queryParams = {} } = {}) => {
  return useQuery(
    [GET_CATEGORIES, params],
    () => workflowService.getCategories(params),
    queryParams
  );
};

export const useGetGroupedWorkflows = ({ id, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_GROUPED_WORKFLOWS', id],
    () => workflowService.getGroupedWorkflows(id),
    queryParams
  );
};

export const useGetWorkflowsFunds = ({ params, queryParams = {} } = {}) => {
  return useQuery(
    ['useGetWorkflowsFunds', params],
    () => workflowService.getWorkflowsFunds(params),
    queryParams
  );
};

export const useGetTemplatesList = ({ params = {}, queryParams, workflowId } = {}) => {
  return useQuery(
    [GET_TEMPLATE, params, workflowId],
    () => workflowService.getTemplates({ params, workflowId }),
    {
      ...queryParams
    }
  );
};

export const useGetWorfklowFunds = ({ params = {}, queryParams } = {}) => {
  return useQuery([GET_WORKFLOW, 'funds', params], () => workflowService.getWorkflowFunds(params), {
    ...queryParams
  });
};

export const useGetTransforms = ({
  params = {},
  queryParams,
  workflowId,
  fileId,
  sheetId,
  extractId
} = {}) => {
  return useQuery(
    [GET_TRANSFORM, 'files', params, workflowId, fileId, sheetId, extractId],
    () =>
      workflowService.getTransformations({
        params,
        workflowId,
        fileId,
        sheetId,
        extractId
      }),
    {
      ...queryParams
    }
  );
};

export const useGetTransform = ({ params = {}, queryParams, transformId } = {}) => {
  return useQuery(
    [GET_TRANSFORM, 'item', params, transformId],
    transformId
      ? () =>
          workflowService.getTransformation({
            params,
            transformId
          })
      : () => {},
    {
      enabled: !!transformId,
      ...queryParams
    }
  );
};

export const usePreviewTransform = ({ params = {}, queryParams, transformId, steps } = {}) => {
  return useQuery(
    [GET_TRANSFORM, 'preview', params, transformId, steps],
    transformId
      ? () =>
          workflowService.previewTransformation({
            data: { steps },
            params,
            transformId
          })
      : () => {},
    {
      enabled: !!transformId && !!steps,
      ...queryParams
    }
  );
};

export const useCreateTransform = ({ queryParams }) => {
  return useMutation(workflowService.createTransform, queryParams);
};

export const useUpdateTransform = ({ queryParams }) => {
  return useMutation(workflowService.updateTransform, queryParams);
};

export const useGetTransformationVersions = ({
  transformId,
  params = {},
  queryParams = {}
} = {}) => {
  return useQuery(
    ['GET_TRANSFORMATION_VERSIONS', transformId, params],
    () => workflowService.getTransformationVersions({ transformId, params }),
    {
      enabled: !!transformId,
      ...queryParams
    }
  );
};

export const useGetTransformationVersionChange = ({
  transformId,
  versionId,
  queryParams = {}
} = {}) => {
  return useQuery(
    ['GET_TRANSFORMATION_VERSION_CHANGE', transformId, versionId],
    () => workflowService.getTransformationVersionChange({ transformId, versionId }),
    {
      enabled: !!transformId && !!versionId,
      ...queryParams
    }
  );
};

export const useRestoreTransformationVersion = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.restoreTransformationVersion, queryParams);
};

export const useCreateWorkflow = () => {
  return useMutation(workflowService.createWorkflow);
};

export const useReExtractFunds = ({ queryParams = {} } = {}) => {
  return useMutation(
    ({ sheetId, data }) => workflowService.reExtractFunds({ sheetId, data }),
    queryParams
  );
};

export const useUpdateWorkflow = ({ queryParams = {} } = {}) => {
  return useMutation(workflowService.updateWorkflow, queryParams);
};

export const useCreateWorkflowFund = ({ params }) => {
  return useMutation((/** @type {any} */ data) =>
    workflowService.createWorkflowFund({ data, params })
  );
};

export const useUpdateGroupedWorkflow = () => {
  return useMutation(workflowService.updateGroupedWorkflows);
};

export const useCreateSheetExtract = () => {
  return useMutation(data => workflowService.createSheetExtract(data));
};

export const useCanceAutoExtract = () => {
  return useMutation(workflowService.cancelAutoExtract);
};

export const useDeleteFunds = () => {
  return useMutation(workflowService.deleteFunds);
};

export const useUpdateSheetExtract = () => {
  return useMutation(data => workflowService.updateSheetExtract(data));
};

export const useDeleteSheetExtract = () => {
  return useMutation(extractId => workflowService.deleteSheetExtract(extractId));
};

export const useUpdateWorkflowFund = () => {
  return useMutation((/** @type {any} */ data) =>
    workflowService.updateWorkflowFund({
      fundId: data.fundId,
      data: data.data
    })
  );
};

export const useDeleteWorkflowFund = () => {
  return useMutation((/** @type {any} */ data) =>
    workflowService.deleteWorkflowFund({ fundId: data })
  );
};

export const useDeleteWorkflow = () => {
  return useMutation(workflowService.deleteWorkflow);
};

export const useDeleteWorkflowGroup = () => {
  return useMutation(workflowService.deleteWorkflowGroup);
};

export const useDeleteCustomCategory = () => {
  return useMutation(workflowService.deleteCustomCategory);
};

export const useCreateCustomCategory = () => {
  return useMutation(workflowService.createCustomCategory);
};

export const useDeleteSheet = () => {
  return useMutation(workflowService.deleteSheet);
};

export const useDuplicateWorkflow = () => {
  return useMutation(workflowService.duplicateWorkflow);
};

export const useDeleteQuarter = () => {
  return useMutation(workflowService.deleteQuarter);
};

export const useLoadDataToSnowflake = () => {
  return useMutation(workflowService.loadDataToSnowflake);
};

export const useGetHeaderFooter = ({ sheetId, params, queryParams }) => {
  return useQuery(
    [GET_HEADER_FOOTER, sheetId, params],
    () => workflowService.getHeaderFooter({ sheetId, params }),
    queryParams
  );
};

export const useSetHeaderFooter = ({ sheetId, queryParams }) => {
  return useMutation(data => workflowService.setHeaderFooter({ sheetId, data }), queryParams);
};

export const useUpdateTableStructure = ({ sheetId, queryParams }) => {
  return useMutation(data => workflowService.updateStructureType({ sheetId, data }), queryParams);
};

export const useGetTransformationsListByPeriod = ({ periodId, params = {}, queryParams = {} }) => {
  return useQuery(
    ['GET_TRANSFORMATIONS_LIST', periodId, params],
    () => workflowService.getTransformationsListByPeriod(periodId, params),
    {
      enabled: !!periodId,
      ...queryParams
    }
  );
};
